import React, { useState, useEffect, useRef } from 'react';
import '../styles/SalesDetailModal.css';  // モーダルのスタイル

const SalesDetailModal = ({
    isOpen,
    onClose,
    selectedItem,
    conditions,
    categorys,
    onSave,
    onChangeQuantity,
    onChangeUnitPrice,
    onChangeCondition,
    onChangeCategory,
    onChangeBoxDamage,
    onChangeSpecialPrice,
    handleOutsideClick,
    onChangeDiscount,
    setBarcodeFocusEnabled
}) => {
    const [focusedInput, setFocusedInput] = useState(''); // フォーカスされているインプットを追跡
    const [isButtonDisabled, setIsButtonDisabled] = useState(true);
    const [errorMessage, setErrorMessage] = useState('');

    const scanBufferRef = useRef("");
    const scanTimerRef = useRef(null);

  useEffect(() => {
    // selectedItemがnullやundefinedでないか確認
    if (selectedItem && selectedItem.product_name !== 'レジ袋' && selectedItem.product_name !== 'ガチャガチャ') {
      let error = '';
      if (!selectedItem.condition || selectedItem.condition === 0) {
        error = 'コンディションが未入力です';
      } else if (!selectedItem.category || selectedItem.category === 0) {
        error = 'カテゴリーが未入力です';
      } else if (!selectedItem.unit_price || selectedItem.unit_price === 0) {
        error = '単価が未入力です';
      } else if (!selectedItem.quantity || selectedItem.quantity === 0) {
        error = '数量が未入力です';
      }

      if (error) {
        setIsButtonDisabled(true);
        setErrorMessage(error);
      } else {
        setIsButtonDisabled(false);
        setErrorMessage('');
      }
    } else {
      // selectedItemがnullまたはundefinedの場合、ボタンを無効にしてエラーメッセージを表示
      setIsButtonDisabled(false);
      setErrorMessage('決定');
    }
  }, [selectedItem]); // selectedItemが変更されるたびに実行されます

    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (e) => {
            // Backspace
            if (e.code === "Backspace") {
                handleBackspace();
                e.preventDefault();
                return;
            }

            // Delete
            if (e.code === "Delete") {
                handleClear();
                e.preventDefault();
                return;
            }

            // Enter
            if (e.code === "NumpadEnter") {
                onSave();
                setBarcodeFocusEnabled(true);
                e.preventDefault();
                return;
            }

            // テンキー数字
            if (!/^Numpad[0-9]$/.test(e.code)) return;

            const num = e.code.replace("Numpad", "");

            scanBufferRef.current += num;

            clearTimeout(scanTimerRef.current);

            scanTimerRef.current = setTimeout(() => {
                const value = scanBufferRef.current;

                scanBufferRef.current = "";

                // 10文字以上はバーコード扱い
                if (value.length >= 10) return;

                handleKeyPress(value);
            }, 80);

            e.preventDefault();
        };

        window.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [isOpen, focusedInput, selectedItem]);

    if (!isOpen || !selectedItem) return null;
    

    // キーパッドのボタンがクリックされたときの処理
    const handleKeyPress = (key) => {
        if (focusedInput === 'quantity') {
            onChangeQuantity({ target: { value: selectedItem.quantity + key } });
        } else if (focusedInput === 'unit_price') {
            onChangeUnitPrice({ target: { value: selectedItem.unit_price + key } });
        } else if (focusedInput === 'discount') {
            onChangeDiscount({ target: { value: selectedItem.discount + key } });    
        }
    };
    
    // クリアボタンがクリックされたときの処理
    const handleClear = () => {
        if (focusedInput === 'quantity') {
            // 数値をクリア
            onChangeQuantity({ target: { value: '' } });
        } else if (focusedInput === 'unit_price') {
            // 数値をクリア
            onChangeUnitPrice({ target: { value: '' } });
        } else if (focusedInput === 'discount') {
            // 数値をクリア
            onChangeDiscount({ target: { value: '' } });    
        }
    };    

    // バックスペースボタンがクリックされたときの処理
    const handleBackspace = () => {
        if (focusedInput === 'quantity') {
            // 数値を文字列に変換してからスライス
            onChangeQuantity({ target: { value: String(selectedItem.quantity).slice(0, -1) } });
        } else if (focusedInput === 'unit_price') {
            // 同様に単価も文字列に変換してからスライス
            onChangeUnitPrice({ target: { value: String(selectedItem.unit_price).slice(0, -1) } });
        } else if (focusedInput === 'discount') {
            // 同様に単価も文字列に変換してからスライス
            onChangeDiscount({ target: { value: String(selectedItem.discount).slice(0, -1) } });    
        }
    };

    return (
        <div className="salesDetailModal" onClick={handleOutsideClick}>
            <div className="modalContent">
                <span className="close" onClick={onClose}>
                    &times;
                </span>
                <label className="salesDetailModal-label-product_name">
                    商品名: {selectedItem.product_name}
                </label>

                {/* 商品状態ボタン群 */}
                <div className="salesDetailModal-condition-buttons">
                    {conditions.map((condition) => (
                        <button
                            key={condition.id}
                            className={selectedItem.condition === condition.id ? 'active' : ''}
                            onClick={() => onChangeCondition(condition.id)}
                        >
                            {condition.condition_jpn}
                        </button>
                    ))}
                </div>

                {/* 箱ダメージボタン */}
                <div className="salesDetailModal-checkbox-button-group">
                    <button
                        className={selectedItem.box_damage ? 'active' : ''}
                        onClick={() => onChangeBoxDamage(!selectedItem.box_damage)}
                    >
                        箱ダメージ
                    </button>
                    {/* 特価ボタン */}
                    <button
                        className={selectedItem.special_price ? 'active' : ''}
                        onClick={() => onChangeSpecialPrice(!selectedItem.special_price)}
                    >
                        特価
                    </button>
                </div>

                {/* カテゴリーボタン群 */}
                <div className="salesDetailModal-category-buttons">
                    {categorys.map((category) => (
                        <button
                            key={category.id}
                            className={selectedItem.category === category.id ? 'active' : ''}
                            onClick={() => onChangeCategory(category.id)}
                        >
                            {category.category_name}
                        </button>
                    ))}
                </div>

                {/* 数量・単価入力 */}
                <div className="salesDetailModal-input-number">
                    <span className="salesDetailModal-quantity">
                        <label>数量</label>
                        <input
                            type="number"
                            value={selectedItem.quantity}
                            onFocus={() => setFocusedInput('quantity')}
                            onChange={onChangeQuantity}
                        />
                    </span>
                    <span className="salesDetailModal-unit_price">
                        <label>単価</label>
                        <input
                            type="number"
                            value={selectedItem.unit_price}
                            onFocus={() => setFocusedInput('unit_price')}
                            onChange={onChangeUnitPrice}
                        />
                    </span>
                    <span className="salesDetailModal-discount">
                        <label>値引き</label>
                        <input
                            type="number"
                            value={selectedItem.discount}
                            onFocus={() => setFocusedInput('discount')}
                            onChange={onChangeDiscount}
                        />
                    </span>
                </div>

                {/* キーパッド */}
                <div className="salesDetailModal-keypad">
                    <div className="alesDetailModal-keypad-buttons">
                    <button className="alesDetailModal-keypad-button-function" onClick={handleClear}>Ｃ</button>
                    <div></div>
                    <button className="alesDetailModal-keypad-button-function" onClick={handleBackspace}>←</button>
                    {[7, 8, 9, 4, 5, 6, 1, 2, 3].map((num) => (
                        <button className="alesDetailModal-keypad-button"
                            key={num}
                            onClick={() => handleKeyPress((num).toString())}
                        >
                            {num}
                        </button>
                    ))}
                    <button className="alesDetailModal-keypad-button" onClick={() => handleKeyPress('0')}>0</button>
                    <button className="alesDetailModal-keypad-button" onClick={() => handleKeyPress('00')}>00</button>
                    </div>
                </div>
                <div className="alesDetailModal-decision-buttons">
                    <button 
                        className="alesDetailModal-decision-button" 
                        onClick={() => {
                            onSave();
                            setBarcodeFocusEnabled(true);
                        }}
                        disabled={isButtonDisabled}>
                        {isButtonDisabled ? errorMessage : '決定'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SalesDetailModal;
